import * as THREE from 'three';
import { locateTrackPosition, type Track } from '../track/TrackLoader';

/** Closed polygon as a list of {x,z} points (CW or CCW, doesn't matter). */
export type Polygon2D = { x: number; z: number }[];

export function pointInPolygon(px: number, pz: number, poly: Polygon2D): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, zi = poly[i].z;
    const xj = poly[j].x, zj = poly[j].z;
    const intersect = (zi > pz) !== (zj > pz)
      && px < ((xj - xi) * (pz - zi)) / ((zj - zi) || 1e-9) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

interface NearestEdge {
  closestX: number;
  closestZ: number;
  nx: number;
  nz: number;
  distSq: number;
}

const TMP_EDGE: NearestEdge = { closestX: 0, closestZ: 0, nx: 0, nz: 0, distSq: 0 };

/** Closest point on polygon edges to (px, pz). Outward normal points away from polygon centroid. */
export function nearestEdge(
  px: number,
  pz: number,
  poly: Polygon2D,
  centroid: { x: number; z: number },
): NearestEdge {
  let best = Infinity;
  let bestX = 0, bestZ = 0, bestNx = 0, bestNz = 0;

  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    const lenSq = ex * ex + ez * ez || 1e-9;
    let t = ((px - a.x) * ex + (pz - a.z) * ez) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const cx = a.x + ex * t;
    const cz = a.z + ez * t;
    const dx = px - cx;
    const dz = pz - cz;
    const dSq = dx * dx + dz * dz;
    if (dSq < best) {
      best = dSq;
      bestX = cx;
      bestZ = cz;
      let nx = -ez;
      let nz = ex;
      const nl = Math.hypot(nx, nz) || 1;
      nx /= nl; nz /= nl;
      const cToC_x = cx - centroid.x;
      const cToC_z = cz - centroid.z;
      if (nx * cToC_x + nz * cToC_z < 0) { nx = -nx; nz = -nz; }
      bestNx = nx; bestNz = nz;
    }
  }

  TMP_EDGE.closestX = bestX;
  TMP_EDGE.closestZ = bestZ;
  TMP_EDGE.nx = bestNx;
  TMP_EDGE.nz = bestNz;
  TMP_EDGE.distSq = best;
  return TMP_EDGE;
}

export interface WallHit {
  hit: boolean;
  impactSpeed: number;
  nx: number;
  nz: number;
}

/**
 * Contact state object — the single source of truth for "what is the racer
 * doing against a wall right now?". One smoothed `intensity` scalar drives
 * audio gain, particle rates, camera roll, rumble, and post-fx so they all
 * read as a single coherent event rather than disconnected systems.
 *
 * Updated once per physics tick from the resolved wall hit; downstream
 * systems read it during render. Persists across frames so smoothing and
 * decay envelopes work correctly.
 */
export interface ContactState {
  /** True if any wall/rail/obstacle resolved a hit this frame. */
  active: boolean;
  /** Outward wall normal — points FROM the wall TOWARD the racer side. */
  outwardNx: number;
  outwardNz: number;
  /** World-space contact point (best estimate; for particle emission). */
  contactX: number;
  contactY: number;
  contactZ: number;
  /** Pre-response velocity component INTO the wall, m/s. */
  normalSpeed: number;
  /** Post-response velocity along the wall, m/s. */
  tangentSpeed: number;
  /** Δv into the wall this frame — the "new hit" magnitude, m/s. */
  impactImpulse: number;
  /** Angle factor: 1 = head-on, 0 = parallel. Used to gate hitstop. */
  impactAngle: number;
  /** Smoothed combined intensity 0..1, ~80 ms time constant. */
  intensity: number;
  /** Sustained contact duration. */
  contactSeconds: number;
  /** Seconds since the last NEW impact transition. */
  timeSinceImpact: number;
}

export function makeContactState(): ContactState {
  return {
    active: false,
    outwardNx: 0,
    outwardNz: 0,
    contactX: 0,
    contactY: 0,
    contactZ: 0,
    normalSpeed: 0,
    tangentSpeed: 0,
    impactImpulse: 0,
    impactAngle: 0,
    intensity: 0,
    contactSeconds: 0,
    timeSinceImpact: 99,
  };
}

export interface ContactStateUpdate {
  hit: WallHit;
  preVelocityX: number;
  preVelocityZ: number;
  postVelocityX: number;
  postVelocityZ: number;
  contactX: number;
  contactY: number;
  contactZ: number;
  contactSeconds: number;
  /** True when wall contact resumed after a frame of no contact. */
  newTransition: boolean;
  dt: number;
}

const INTENSITY_NORMAL_REF = 30;  // m/s into-wall Δv that maps to full intensity
const INTENSITY_TANGENT_REF = 40; // m/s along-wall speed that maps to full intensity
const INTENSITY_TIME_CONSTANT = 0.08;
const INTENSITY_OFF_DECAY_PER_SEC = 8;
/**
 * Presence floor: any active wall contact produces at least this much
 * intensity, regardless of speed. Without it, a slow nudge into the wall
 * registers as `active=true` but with intensity ~0, so the trickle, scrape
 * audio, and camera lean (all gated above ~0.05) never fire and "touching
 * the wall" feels indistinguishable from "free of it." 0.18 puts slow
 * contact comfortably above all gates while still letting impacts spike
 * cleanly above the floor.
 */
const CONTACT_PRESENCE_FLOOR = 0.18;

export function updateContactState(state: ContactState, update: ContactStateUpdate): void {
  const dt = Math.max(0, update.dt);

  if (!update.hit.hit) {
    state.active = false;
    state.normalSpeed = 0;
    state.tangentSpeed = 0;
    state.impactImpulse = 0;
    state.impactAngle = 0;
    state.contactSeconds = 0;
    state.timeSinceImpact = Math.min(99, state.timeSinceImpact + dt);
    state.intensity *= Math.exp(-dt * INTENSITY_OFF_DECAY_PER_SEC);
    if (state.intensity < 0.001) state.intensity = 0;
    return;
  }

  const nLen = Math.hypot(update.hit.nx, update.hit.nz) || 1;
  // hit.nx/nz points INTO the wall by Collision.ts convention; outward = negate.
  const outwardNx = -update.hit.nx / nLen;
  const outwardNz = -update.hit.nz / nLen;

  // Velocity decomposition: into-wall direction is -outward.
  const intoX = -outwardNx;
  const intoZ = -outwardNz;
  const preIntoWall = update.preVelocityX * intoX + update.preVelocityZ * intoZ;
  const postIntoWall = update.postVelocityX * intoX + update.postVelocityZ * intoZ;

  const postNormalX = postIntoWall * intoX;
  const postNormalZ = postIntoWall * intoZ;
  const postTangentX = update.postVelocityX - postNormalX;
  const postTangentZ = update.postVelocityZ - postNormalZ;
  const tangentSpeed = Math.hypot(postTangentX, postTangentZ);

  const impactImpulse = Math.max(0, preIntoWall - postIntoWall);

  const preSpeed = Math.hypot(update.preVelocityX, update.preVelocityZ);
  const angle = preSpeed > 0.5 ? Math.max(0, preIntoWall / preSpeed) : 0;

  const normalIntensity = Math.min(1, impactImpulse / INTENSITY_NORMAL_REF);
  const tangentIntensity = Math.min(1, tangentSpeed / INTENSITY_TANGENT_REF);
  // Presence floor keeps slow contact audible/visible/feelable even when
  // both the impact and slide signals are near zero.
  const targetIntensity = Math.min(
    1,
    Math.max(CONTACT_PRESENCE_FLOOR, 0.7 * normalIntensity + 0.6 * tangentIntensity),
  );

  const smoothAlpha = dt > 0 ? 1 - Math.exp(-dt / INTENSITY_TIME_CONSTANT) : 0;

  state.active = true;
  state.outwardNx = outwardNx;
  state.outwardNz = outwardNz;
  state.contactX = update.contactX;
  state.contactY = update.contactY;
  state.contactZ = update.contactZ;
  state.normalSpeed = Math.max(0, preIntoWall);
  state.tangentSpeed = tangentSpeed;
  state.impactImpulse = impactImpulse;
  state.impactAngle = angle;
  state.intensity = state.intensity + (targetIntensity - state.intensity) * smoothAlpha;
  // Snap intensity up to the floor on the first frame of contact so the
  // trickle / audio / camera lean fire immediately rather than ramping up
  // over the smoothing time constant. Subsequent frames smooth normally.
  if (update.newTransition && state.intensity < CONTACT_PRESENCE_FLOOR) {
    state.intensity = CONTACT_PRESENCE_FLOOR;
  }
  state.contactSeconds = update.contactSeconds;
  state.timeSinceImpact = update.newTransition ? 0 : Math.min(99, state.timeSinceImpact + dt);
}

export interface WallContactResponseTuning {
  collisionRestitution: number;
  collisionSlideFriction: number;
  collisionContactFriction: number;
  collisionPersistentSeconds: number;
  collisionMinBounceSpeed: number;
  collisionSeparationSpeed: number;
  /**
   * Per-second decay rate for the grind friction once contact has been
   * sustained beyond `collisionPersistentSeconds`. 0 = no decay (legacy);
   * higher values release the wall faster. Prevents the wall-magnet feel on
   * long scrapes without softening the initial bite.
   */
  collisionContactFrictionRecovery?: number;
  /**
   * Maximum velocity (m/s) that can be removed from the racer in one
   * physics step on a hard hit. When set, big impacts bleed across several
   * frames instead of dumping in one — this gives the camera spring time
   * to follow and removes the "brick wall stutter" feel. 0 / undefined =
   * legacy single-frame absorption.
   */
  collisionMaxFrameLoss?: number;
}

/**
 * Final arcade contact pass for a wall/rail hit. Collision resolvers do the
 * hard work of finding and correcting penetration; this pass shapes the
 * velocity into the feel players expect from racing walls:
 *
 * - first hard impact: configurable bounce plus tangent friction
 * - repeated contact: remove wall-facing velocity and grind/slide along it
 */
export function applyWallContactResponse(
  velocity: THREE.Vector3,
  hit: WallHit,
  dt: number,
  tuning: WallContactResponseTuning,
  contactSeconds = 0,
): void {
  if (!hit.hit) return;
  if (!Number.isFinite(dt) || dt < 0) return;
  const nLen = Math.hypot(hit.nx, hit.nz);
  if (nLen <= 1e-6) return;

  const nx = hit.nx / nLen;
  const nz = hit.nz / nLen;
  const normalSpeed = velocity.x * nx + velocity.z * nz;
  const tangentX = velocity.x - normalSpeed * nx;
  const tangentZ = velocity.z - normalSpeed * nz;

  const restitution = THREE.MathUtils.clamp(tuning.collisionRestitution, 0, 0.85);
  const minBounceSpeed = Math.max(0, tuning.collisionMinBounceSpeed);
  const persistentSeconds = Math.max(0, tuning.collisionPersistentSeconds);
  const isPersistent = contactSeconds >= persistentSeconds || hit.impactSpeed < minBounceSpeed;
  const maxSeparationSpeed = Math.max(0, tuning.collisionSeparationSpeed);

  let nextNormalSpeed = normalSpeed;
  let tangentFactor = 1;
  if (isPersistent) {
    // Into-wall velocity is killed. Existing away-from-wall velocity is kept
    // only up to a tiny separation speed so contact doesn't buzz.
    nextNormalSpeed = Math.min(0, normalSpeed);
    nextNormalSpeed = Math.max(nextNormalSpeed, -maxSeparationSpeed);
    const recovery = Math.max(0, tuning.collisionContactFrictionRecovery ?? 0);
    const sustained = Math.max(0, contactSeconds - persistentSeconds);
    const frictionScale = recovery > 0 ? Math.exp(-recovery * sustained) : 1;
    const effectiveFriction = Math.max(0, tuning.collisionContactFriction) * frictionScale;
    tangentFactor = 1 / (1 + effectiveFriction * Math.max(0, dt));
  } else {
    const fullBounce = -hit.impactSpeed * restitution;
    const fullLoss = normalSpeed - fullBounce;
    // Optional per-step loss cap stretches a hard impact across multiple
    // frames so the camera doesn't have to chase a single 70+ m/s velocity
    // jump in 8 ms. Residual into-wall velocity carries to the next step,
    // where the persistent-mode branch (or another first-hit clamp) kills it.
    const maxLoss = Math.max(0, tuning.collisionMaxFrameLoss ?? 0);
    if (maxLoss > 0 && fullLoss > maxLoss) {
      nextNormalSpeed = normalSpeed - maxLoss;
    } else {
      nextNormalSpeed = fullBounce;
    }
    const impact01 = THREE.MathUtils.clamp(
      (hit.impactSpeed - minBounceSpeed) / Math.max(1, minBounceSpeed * 3),
      0,
      1,
    );
    tangentFactor = Math.max(0, 1 - THREE.MathUtils.clamp(tuning.collisionSlideFriction, 0, 1) * impact01);
  }

  velocity.x = tangentX * tangentFactor + nx * nextNormalSpeed;
  velocity.z = tangentZ * tangentFactor + nz * nextNormalSpeed;
}

export interface MovementSegment2D {
  fromX: number;
  fromZ: number;
  toX: number;
  toZ: number;
}

interface SweptBarrierHit {
  t: number;
  closestX: number;
  closestZ: number;
  nx: number;
  nz: number;
  barrier: SoftBarrier;
}

interface SweptObstacleHit {
  t: number;
  x: number;
  z: number;
  nOutX: number;
  nOutZ: number;
  obstacle: ObstacleObb;
}

const COLLISION_SKIN = 0.02;
const OBSTACLE_COLLISION_SKIN = 0.05;

function validMovementSegment(sweep: MovementSegment2D | undefined): sweep is MovementSegment2D {
  if (!sweep) return false;
  return Number.isFinite(sweep.fromX)
    && Number.isFinite(sweep.fromZ)
    && Number.isFinite(sweep.toX)
    && Number.isFinite(sweep.toZ)
    && Math.hypot(sweep.toX - sweep.fromX, sweep.toZ - sweep.fromZ) > 1e-6;
}

function applyIncomingWallDamping(
  velocity: THREE.Vector3,
  nWallX: number,
  nWallZ: number,
  perpDamping: number,
): number {
  const vDotN = velocity.x * nWallX + velocity.z * nWallZ;
  const impactSpeed = Math.max(0, vDotN);
  if (vDotN > 0) {
    const tx = velocity.x - vDotN * nWallX;
    const tz = velocity.z - vDotN * nWallZ;
    const perpX = -vDotN * nWallX * (1 - perpDamping);
    const perpZ = -vDotN * nWallZ * (1 - perpDamping);
    velocity.x = tx + perpX;
    velocity.z = tz + perpZ;
  }
  return impactSpeed;
}

function betterSweepHit(candidate: SweptBarrierHit, best: SweptBarrierHit | null): SweptBarrierHit {
  return best === null || candidate.t < best.t ? candidate : best;
}

function sweepMovingPointAgainstCircle(
  sweep: MovementSegment2D,
  cx: number,
  cz: number,
  radius: number,
): { t: number; nx: number; nz: number; closestX: number; closestZ: number } | null {
  const dx = sweep.toX - sweep.fromX;
  const dz = sweep.toZ - sweep.fromZ;
  const fx = sweep.fromX - cx;
  const fz = sweep.fromZ - cz;
  const a = dx * dx + dz * dz;
  if (a <= 1e-12) return null;
  const c = fx * fx + fz * fz - radius * radius;
  if (c <= 0) return null;
  const b = 2 * (fx * dx + fz * dz);
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  if (t < 0 || t > 1) return null;
  const hitX = sweep.fromX + dx * t;
  const hitZ = sweep.fromZ + dz * t;
  const nx = (hitX - cx) / radius;
  const nz = (hitZ - cz) / radius;
  const len = Math.hypot(nx, nz);
  if (len <= 1e-6) return null;
  return {
    t,
    nx: nx / len,
    nz: nz / len,
    closestX: cx,
    closestZ: cz,
  };
}

function findSweptSoftBarrierHit(
  sweep: MovementSegment2D,
  barriers: readonly SoftBarrier[],
  radius: number,
): SweptBarrierHit | null {
  let best: SweptBarrierHit | null = null;
  const dx = sweep.toX - sweep.fromX;
  const dz = sweep.toZ - sweep.fromZ;

  for (const barrier of barriers) {
    const line = barrier.line;
    for (let i = 0; i < line.length - 1; i++) {
      const a = line[i];
      const b = line[i + 1];
      const ex = b.x - a.x;
      const ez = b.z - a.z;
      const len = Math.hypot(ex, ez);
      if (len <= 1e-6) continue;

      const ux = ex / len;
      const uz = ez / len;
      const normalX = -uz;
      const normalZ = ux;
      const startSigned = (sweep.fromX - a.x) * normalX + (sweep.fromZ - a.z) * normalZ;
      const endSigned = (sweep.toX - a.x) * normalX + (sweep.toZ - a.z) * normalZ;
      const signedDelta = endSigned - startSigned;

      if (Math.abs(startSigned) > radius && startSigned * signedDelta < 0) {
        const side = Math.sign(startSigned);
        const targetSigned = side * radius;
        const t = (targetSigned - startSigned) / signedDelta;
        if (t >= 0 && t <= 1) {
          const hitX = sweep.fromX + dx * t;
          const hitZ = sweep.fromZ + dz * t;
          const along = ((hitX - a.x) * ux + (hitZ - a.z) * uz);
          if (along >= 0 && along <= len) {
            const nx = normalX * side;
            const nz = normalZ * side;
            best = betterSweepHit({
              t,
              closestX: a.x + ux * along,
              closestZ: a.z + uz * along,
              nx,
              nz,
              barrier,
            }, best);
          }
        }
      }

      const endpointA = sweepMovingPointAgainstCircle(sweep, a.x, a.z, radius);
      if (endpointA) {
        best = betterSweepHit({ ...endpointA, barrier }, best);
      }
      const endpointB = sweepMovingPointAgainstCircle(sweep, b.x, b.z, radius);
      if (endpointB) {
        best = betterSweepHit({ ...endpointB, barrier }, best);
      }
    }
  }
  return best;
}

/**
 * The drivable corridor is the ring between `wallOuter` and `wallInner`.
 * If (px, pz) escapes the corridor (outside outer OR inside inner), push
 * back toward the corridor along the appropriate wall edge normal.
 */
export function resolveCorridorCollision(
  position: THREE.Vector3,
  velocity: THREE.Vector3,
  wallOuter: Polygon2D,
  wallInner: Polygon2D,
  centroid: { x: number; z: number },
  perpDamping = 0.3,
): WallHit {
  const px = position.x;
  const pz = position.z;

  const insideOuter = pointInPolygon(px, pz, wallOuter);
  const insideInner = pointInPolygon(px, pz, wallInner);

  if (insideOuter && !insideInner) {
    return { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
  }

  // `nearestEdge` returns the polygon's outward normal (pointing away from
  // `centroid`). For the OUTER wall, "into the corridor" = -outward.
  // For the INNER wall, "into the corridor" = +outward (away from track center).
  const target = !insideOuter ? wallOuter : wallInner;
  const inwardSign = !insideOuter ? -1 : +1;
  const e = nearestEdge(px, pz, target, centroid);

  const inwardX = e.nx * inwardSign;
  const inwardZ = e.nz * inwardSign;
  position.x = e.closestX + inwardX * 0.05;
  position.z = e.closestZ + inwardZ * 0.05;

  // Wall normal points from corridor INTO the wall.
  const nWallX = -inwardX;
  const nWallZ = -inwardZ;
  const vDotN = velocity.x * nWallX + velocity.z * nWallZ;
  const impactSpeed = Math.max(0, vDotN);
  const tx = velocity.x - vDotN * nWallX;
  const tz = velocity.z - vDotN * nWallZ;
  let perpX = 0, perpZ = 0;
  if (vDotN > 0) {
    perpX = -vDotN * nWallX * (1 - perpDamping);
    perpZ = -vDotN * nWallZ * (1 - perpDamping);
  }
  velocity.x = tx + perpX;
  velocity.z = tz + perpZ;

  return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ };
}

/**
 * Solid obstacle as an oriented bounding box on the XZ plane. `axisAcross` is
 * the local "across" axis (unit vec); the perpendicular CCW axis is "along".
 * `halfAcross` / `halfAlong` are extents along those axes. Y is ignored — we
 * only care about top-down collision for racer-vs-scenery hits.
 */
export interface ObstacleObb {
  centerX: number;
  centerZ: number;
  axisAcrossX: number;
  axisAcrossZ: number;
  halfAcross: number;
  halfAlong: number;
}

/**
 * Inflates each obstacle by `racerRadius` and tests if the racer's center is
 * inside any of them. Push out along the closest face normal and damp the
 * inward velocity component, mirroring the wall-collision response so the
 * impact reads consistently in audio/haptics.
 *
 * Stops at the first hit — obstacles are sparse (one bridge today) so we
 * don't need a broad-phase.
 */
export function resolveObstacleCollision(
  position: THREE.Vector3,
  velocity: THREE.Vector3,
  obstacles: readonly ObstacleObb[],
  racerRadius: number,
  perpDamping = 0.3,
  sweep?: MovementSegment2D,
): WallHit {
  if (validMovementSegment(sweep)) {
    const sweptHit = findSweptObstacleHit(sweep, obstacles, racerRadius);
    if (sweptHit) {
      position.x = sweptHit.x;
      position.z = sweptHit.z;
      const nWallX = -sweptHit.nOutX;
      const nWallZ = -sweptHit.nOutZ;
      const impactSpeed = applyIncomingWallDamping(
        velocity,
        nWallX,
        nWallZ,
        perpDamping,
      );
      return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ };
    }
  }

  for (const o of obstacles) {
    const dx = position.x - o.centerX;
    const dz = position.z - o.centerZ;
    // Project (dx, dz) onto the obstacle's local axes.
    // axisAlong = rotate axisAcross by +90° CCW = (-axisAcrossZ, +axisAcrossX).
    const lx = dx * o.axisAcrossX + dz * o.axisAcrossZ;
    const ly = dx * (-o.axisAcrossZ) + dz * o.axisAcrossX;
    const inflatedAcross = o.halfAcross + racerRadius;
    const inflatedAlong = o.halfAlong + racerRadius;
    if (Math.abs(lx) > inflatedAcross || Math.abs(ly) > inflatedAlong) continue;

    // Pick the face whose outward direction is the SHALLOWEST penetration —
    // that's the closest exit. Tie-break to "across" so legs eject the racer
    // sideways (toward the road), not back along the racing direction.
    const penAcross = inflatedAcross - Math.abs(lx);
    const penAlong = inflatedAlong - Math.abs(ly);
    let nLocalAcross = 0;
    let nLocalAlong = 0;
    if (penAcross <= penAlong) {
      nLocalAcross = lx >= 0 ? 1 : -1;
    } else {
      nLocalAlong = ly >= 0 ? 1 : -1;
    }

    // Outward normal in world space.
    const nOutX = nLocalAcross * o.axisAcrossX + nLocalAlong * (-o.axisAcrossZ);
    const nOutZ = nLocalAcross * o.axisAcrossZ + nLocalAlong * o.axisAcrossX;

    // Place racer center on the inflated face, +5cm air gap.
    const newLx = nLocalAcross !== 0 ? nLocalAcross * (inflatedAcross + 0.05) : lx;
    const newLy = nLocalAlong !== 0 ? nLocalAlong * (inflatedAlong + 0.05) : ly;
    position.x = o.centerX + newLx * o.axisAcrossX + newLy * (-o.axisAcrossZ);
    position.z = o.centerZ + newLx * o.axisAcrossZ + newLy * o.axisAcrossX;

    // Convention: nWall points INTO the obstacle (matches resolveTrackCorridorCollision).
    const nWallX = -nOutX;
    const nWallZ = -nOutZ;
    const vDotN = velocity.x * nWallX + velocity.z * nWallZ;
    const impactSpeed = Math.max(0, vDotN);
    if (vDotN > 0) {
      const tx = velocity.x - vDotN * nWallX;
      const tz = velocity.z - vDotN * nWallZ;
      const perpX = -vDotN * nWallX * (1 - perpDamping);
      const perpZ = -vDotN * nWallZ * (1 - perpDamping);
      velocity.x = tx + perpX;
      velocity.z = tz + perpZ;
    }

    return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ };
  }
  return { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
}

function findSweptObstacleHit(
  sweep: MovementSegment2D,
  obstacles: readonly ObstacleObb[],
  racerRadius: number,
): SweptObstacleHit | null {
  let best: SweptObstacleHit | null = null;
  for (const obstacle of obstacles) {
    const hit = sweepMovingPointAgainstInflatedObb(sweep, obstacle, racerRadius);
    if (!hit) continue;
    if (best === null || hit.t < best.t) best = hit;
  }
  return best;
}

function sweepMovingPointAgainstInflatedObb(
  sweep: MovementSegment2D,
  obstacle: ObstacleObb,
  racerRadius: number,
): SweptObstacleHit | null {
  const alongX = -obstacle.axisAcrossZ;
  const alongZ = obstacle.axisAcrossX;
  const sxWorld = sweep.fromX - obstacle.centerX;
  const szWorld = sweep.fromZ - obstacle.centerZ;
  const exWorld = sweep.toX - obstacle.centerX;
  const ezWorld = sweep.toZ - obstacle.centerZ;
  const sx = sxWorld * obstacle.axisAcrossX + szWorld * obstacle.axisAcrossZ;
  const sy = sxWorld * alongX + szWorld * alongZ;
  const ex = exWorld * obstacle.axisAcrossX + ezWorld * obstacle.axisAcrossZ;
  const ey = exWorld * alongX + ezWorld * alongZ;
  const dx = ex - sx;
  const dy = ey - sy;
  const hx = obstacle.halfAcross + racerRadius;
  const hy = obstacle.halfAlong + racerRadius;

  if (Math.abs(sx) <= hx && Math.abs(sy) <= hy) return null;

  let enterT = 0;
  let exitT = 1;
  let nLocalX = 0;
  let nLocalY = 0;

  const clipAxis = (
    start: number,
    delta: number,
    halfExtent: number,
    axis: 'x' | 'y',
  ): boolean => {
    if (Math.abs(delta) <= 1e-9) {
      return start >= -halfExtent && start <= halfExtent;
    }
    const near = delta > 0
      ? { t: (-halfExtent - start) / delta, n: -1 }
      : { t: (halfExtent - start) / delta, n: 1 };
    const far = delta > 0
      ? (halfExtent - start) / delta
      : (-halfExtent - start) / delta;
    if (near.t > enterT) {
      enterT = near.t;
      nLocalX = axis === 'x' ? near.n : 0;
      nLocalY = axis === 'y' ? near.n : 0;
    }
    exitT = Math.min(exitT, far);
    return enterT <= exitT;
  };

  if (!clipAxis(sx, dx, hx, 'x')) return null;
  if (!clipAxis(sy, dy, hy, 'y')) return null;
  if (enterT < 0 || enterT > 1) return null;

  const hitLocalX = sx + dx * enterT;
  const hitLocalY = sy + dy * enterT;
  const resolvedLocalX = nLocalX !== 0
    ? nLocalX * (hx + OBSTACLE_COLLISION_SKIN)
    : THREE.MathUtils.clamp(hitLocalX, -hx, hx);
  const resolvedLocalY = nLocalY !== 0
    ? nLocalY * (hy + OBSTACLE_COLLISION_SKIN)
    : THREE.MathUtils.clamp(hitLocalY, -hy, hy);
  const nOutX = nLocalX * obstacle.axisAcrossX + nLocalY * alongX;
  const nOutZ = nLocalX * obstacle.axisAcrossZ + nLocalY * alongZ;
  const nLen = Math.hypot(nOutX, nOutZ);
  if (nLen <= 1e-6) return null;

  return {
    t: enterT,
    x: obstacle.centerX + resolvedLocalX * obstacle.axisAcrossX + resolvedLocalY * alongX,
    z: obstacle.centerZ + resolvedLocalX * obstacle.axisAcrossZ + resolvedLocalY * alongZ,
    nOutX: nOutX / nLen,
    nOutZ: nOutZ / nLen,
    obstacle,
  };
}

/** Open polyline (chain of {x,z}) used for soft barriers like guard rails. */
export type Polyline2D = { x: number; z: number }[];

export interface SoftBarrier {
  line: Polyline2D;
  /** [0..1] — share of inward velocity absorbed when penetrating. */
  perpDamping: number;
  /** Per-second forward-velocity drag while scraping (e.g. 0.20 = -20%/sec). */
  scrapeDrag: number;
  /** Lateral threshold past the racer body where scraping begins. */
  contactRadius: number;
}

interface NearestPolylineHit {
  closestX: number;
  closestZ: number;
  /** Normal pointing FROM the line TOWARD the query point (unit). Zero if degenerate. */
  nx: number;
  nz: number;
  dist: number;
}

function nearestPointOnPolyline(
  px: number,
  pz: number,
  line: Polyline2D,
  out: NearestPolylineHit,
): boolean {
  if (line.length < 2) return false;
  let best = Infinity;
  let bestX = 0, bestZ = 0;
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i];
    const b = line[i + 1];
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    const lenSq = ex * ex + ez * ez || 1e-9;
    let t = ((px - a.x) * ex + (pz - a.z) * ez) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const cx = a.x + ex * t;
    const cz = a.z + ez * t;
    const dx = px - cx;
    const dz = pz - cz;
    const dSq = dx * dx + dz * dz;
    if (dSq < best) {
      best = dSq;
      bestX = cx;
      bestZ = cz;
    }
  }
  const dist = Math.sqrt(best);
  out.closestX = bestX;
  out.closestZ = bestZ;
  out.dist = dist;
  if (dist > 1e-6) {
    out.nx = (px - bestX) / dist;
    out.nz = (pz - bestZ) / dist;
  } else {
    out.nx = 0;
    out.nz = 0;
  }
  return true;
}

const TMP_POLYLINE_HIT: NearestPolylineHit = {
  closestX: 0,
  closestZ: 0,
  nx: 0,
  nz: 0,
  dist: 0,
};

export interface SoftBarrierHit extends WallHit {
  scraping: boolean;
}

const NO_SOFT_HIT: SoftBarrierHit = {
  hit: false,
  impactSpeed: 0,
  nx: 0,
  nz: 0,
  scraping: false,
};

/**
 * Resolves the racer against soft scrape barriers (e.g. guard rails). The
 * line is treated as 2-sided — whichever side the racer is on, the line
 * pushes them away.
 *
 * Behavior in three zones:
 *   dist < racerRadius                 -> penetration: push out + perpDamping
 *   dist < racerRadius + contactRadius -> scraping: scrapeDrag on forward vel
 *   else                               -> no contact
 *
 * Stops at first interacting barrier (rails are sparse, no broad-phase needed).
 */
export function resolveSoftScrapeCollision(
  position: THREE.Vector3,
  velocity: THREE.Vector3,
  barriers: readonly SoftBarrier[],
  racerRadius: number,
  dt: number,
  sweep?: MovementSegment2D,
): SoftBarrierHit {
  if (validMovementSegment(sweep)) {
    const sweptHit = findSweptSoftBarrierHit(sweep, barriers, racerRadius);
    if (sweptHit) {
      position.x = sweptHit.closestX + sweptHit.nx * (racerRadius + COLLISION_SKIN);
      position.z = sweptHit.closestZ + sweptHit.nz * (racerRadius + COLLISION_SKIN);
      const nWallX = -sweptHit.nx;
      const nWallZ = -sweptHit.nz;
      const impactSpeed = applyIncomingWallDamping(
        velocity,
        nWallX,
        nWallZ,
        sweptHit.barrier.perpDamping,
      );
      return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ, scraping: false };
    }
  }

  for (const barrier of barriers) {
    if (!nearestPointOnPolyline(position.x, position.z, barrier.line, TMP_POLYLINE_HIT)) continue;
    const { dist, nx, nz, closestX, closestZ } = TMP_POLYLINE_HIT;
    const penetrating = dist < racerRadius;
    const scraping = !penetrating && dist < racerRadius + barrier.contactRadius;
    if (!penetrating && !scraping) continue;
    if (nx === 0 && nz === 0) continue; // degenerate (racer exactly on line)

    // Wall normal points from corridor (racer side) INTO the barrier.
    const nWallX = -nx;
    const nWallZ = -nz;
    const vDotN = velocity.x * nWallX + velocity.z * nWallZ;
    const impactSpeed = Math.max(0, vDotN);

    if (penetrating) {
      position.x = closestX + nx * (racerRadius + 0.02);
      position.z = closestZ + nz * (racerRadius + 0.02);
      if (vDotN > 0) {
        const tx = velocity.x - vDotN * nWallX;
        const tz = velocity.z - vDotN * nWallZ;
        const perpX = -vDotN * nWallX * (1 - barrier.perpDamping);
        const perpZ = -vDotN * nWallZ * (1 - barrier.perpDamping);
        velocity.x = tx + perpX;
        velocity.z = tz + perpZ;
      }
      return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ, scraping: false };
    }

    // Scrape drag: scales linearly with contact depth so a glancing pass
    // barely slows you while a hard graze pulls hard. Applies regardless
    // of velocity direction — chassis-to-rail friction is symmetric.
    const contactDepth = (racerRadius + barrier.contactRadius) - dist;
    const depthFrac = Math.max(0, Math.min(1, contactDepth / Math.max(barrier.contactRadius, 1e-6)));
    const factor = Math.max(0, 1 - barrier.scrapeDrag * depthFrac * dt);
    velocity.x *= factor;
    velocity.z *= factor;
    return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ, scraping: true };
  }
  return { ...NO_SOFT_HIT };
}

/**
 * Off-track terrain interaction. The racer can stand on terrain whose local
 * gradient is shallower than `maxClimbRad`; on steeper terrain the racer is
 * pushed back along the down-gradient direction (terrain acts as a wall).
 *
 * Local gradient (not a heading-relative forward probe) so a racer drifting
 * parallel to a cliff is still blocked by it.
 *
 * Caller is responsible for gating this on `onTrack === false` — when on
 * track, the smoothed track surface is authoritative.
 */
export function resolveTerrainSlopeCollision(
  position: THREE.Vector3,
  velocity: THREE.Vector3,
  sampleHeight: (x: number, z: number) => number,
  maxClimbRad: number,
  perpDamping = 0.85,
): WallHit {
  const groundY = sampleHeight(position.x, position.z);
  const eps = 1.0;
  const dhX = sampleHeight(position.x + eps, position.z) - groundY;
  const dhZ = sampleHeight(position.x, position.z + eps) - groundY;
  const slopeMag = Math.atan2(Math.hypot(dhX, dhZ), eps);

  if (slopeMag < maxClimbRad) {
    position.y = groundY;
    return { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
  }

  let gradX = dhX / eps;
  let gradZ = dhZ / eps;
  const gLen = Math.hypot(gradX, gradZ);
  if (gLen < 1e-4) {
    position.y = groundY;
    return { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
  }
  gradX /= gLen;
  gradZ /= gLen;
  // Wall normal points UP-slope; into the wall = up-slope direction.
  const nWallX = gradX;
  const nWallZ = gradZ;
  const vDotN = velocity.x * nWallX + velocity.z * nWallZ;
  const impactSpeed = Math.max(0, vDotN);
  if (vDotN > 0) {
    const tx = velocity.x - vDotN * nWallX;
    const tz = velocity.z - vDotN * nWallZ;
    const perpX = -vDotN * nWallX * (1 - perpDamping);
    const perpZ = -vDotN * nWallZ * (1 - perpDamping);
    velocity.x = tx + perpX;
    velocity.z = tz + perpZ;
  }
  position.x -= nWallX * 0.1;
  position.z -= nWallZ * 0.1;
  // Re-snap Y to the post-pushback ground so the racer doesn't float.
  position.y = sampleHeight(position.x, position.z);
  return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ };
}

/**
 * 2D triangle in the XZ plane — extracted from a 3D mesh and used for
 * direct mesh-vs-racer collision. Each triangle stores its 3 vertices and
 * an axis-aligned bounding box for cheap broad-phase rejection.
 */
export interface MeshTriangle2D {
  ax: number; az: number;
  bx: number; bz: number;
  cx: number; cz: number;
  minX: number; minZ: number;
  maxX: number; maxZ: number;
}

/** Collection of triangles plus a coarse AABB so callers can early-out. */
export interface MeshCollider {
  triangles: MeshTriangle2D[];
  minX: number; minZ: number;
  maxX: number; maxZ: number;
}

export function buildMeshCollider(triangles: MeshTriangle2D[]): MeshCollider {
  let minX = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxZ = -Infinity;
  for (const t of triangles) {
    if (t.minX < minX) minX = t.minX;
    if (t.minZ < minZ) minZ = t.minZ;
    if (t.maxX > maxX) maxX = t.maxX;
    if (t.maxZ > maxZ) maxZ = t.maxZ;
  }
  return { triangles, minX, minZ, maxX, maxZ };
}

interface ClosestPoint {
  x: number;
  z: number;
  distSq: number;
}

const TMP_CLOSEST: ClosestPoint = { x: 0, z: 0, distSq: 0 };

/** Closest point on a 2D triangle to (px, pz). Standard algorithm. */
function closestPointOnTriangle2D(
  px: number,
  pz: number,
  t: MeshTriangle2D,
  out: ClosestPoint,
): void {
  // Edge AB
  const abx = t.bx - t.ax;
  const abz = t.bz - t.az;
  const acx = t.cx - t.ax;
  const acz = t.cz - t.az;
  const apx = px - t.ax;
  const apz = pz - t.az;
  const d1 = abx * apx + abz * apz;
  const d2 = acx * apx + acz * apz;
  if (d1 <= 0 && d2 <= 0) {
    out.x = t.ax; out.z = t.az;
    const dx = px - t.ax, dz = pz - t.az;
    out.distSq = dx * dx + dz * dz;
    return;
  }
  // Vertex B region
  const bpx = px - t.bx;
  const bpz = pz - t.bz;
  const d3 = abx * bpx + abz * bpz;
  const d4 = acx * bpx + acz * bpz;
  if (d3 >= 0 && d4 <= d3) {
    out.x = t.bx; out.z = t.bz;
    const dx = px - t.bx, dz = pz - t.bz;
    out.distSq = dx * dx + dz * dz;
    return;
  }
  // Edge AB region
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3);
    out.x = t.ax + abx * v;
    out.z = t.az + abz * v;
    const dx = px - out.x, dz = pz - out.z;
    out.distSq = dx * dx + dz * dz;
    return;
  }
  // Vertex C region
  const cpx = px - t.cx;
  const cpz = pz - t.cz;
  const d5 = abx * cpx + abz * cpz;
  const d6 = acx * cpx + acz * cpz;
  if (d6 >= 0 && d5 <= d6) {
    out.x = t.cx; out.z = t.cz;
    const dx = px - t.cx, dz = pz - t.cz;
    out.distSq = dx * dx + dz * dz;
    return;
  }
  // Edge AC region
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const w = d2 / (d2 - d6);
    out.x = t.ax + acx * w;
    out.z = t.az + acz * w;
    const dx = px - out.x, dz = pz - out.z;
    out.distSq = dx * dx + dz * dz;
    return;
  }
  // Edge BC region
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    const w = (d4 - d3) / ((d4 - d3) + (d5 - d6));
    out.x = t.bx + (t.cx - t.bx) * w;
    out.z = t.bz + (t.cz - t.bz) * w;
    const dx = px - out.x, dz = pz - out.z;
    out.distSq = dx * dx + dz * dz;
    return;
  }
  // Inside triangle: closest point IS the query point. distSq = 0 forces
  // the resolver to fall through; the caller treats this as a deep
  // penetration and pushes out via the longest available edge.
  out.x = px;
  out.z = pz;
  out.distSq = 0;
}

/**
 * Push the racer out of any mesh triangle whose footprint overlaps the
 * racer's chassis circle (`racerRadius`). Stops at first hit — for one
 * bridge or scenery model, broad-phase AABB rejection keeps this cheap.
 *
 * Triangles are world-space, projected to the XZ plane at extraction time.
 */
export function resolveMeshCollision(
  position: THREE.Vector3,
  velocity: THREE.Vector3,
  colliders: readonly MeshCollider[],
  racerRadius: number,
  perpDamping: number,
): WallHit {
  const px = position.x;
  const pz = position.z;
  const radSq = racerRadius * racerRadius;

  for (const collider of colliders) {
    // Coarse AABB rejection.
    if (px + racerRadius < collider.minX || px - racerRadius > collider.maxX) continue;
    if (pz + racerRadius < collider.minZ || pz - racerRadius > collider.maxZ) continue;

    for (const tri of collider.triangles) {
      // Per-triangle AABB rejection.
      if (px + racerRadius < tri.minX || px - racerRadius > tri.maxX) continue;
      if (pz + racerRadius < tri.minZ || pz - racerRadius > tri.maxZ) continue;

      closestPointOnTriangle2D(px, pz, tri, TMP_CLOSEST);
      if (TMP_CLOSEST.distSq > radSq) continue;
      // Skip degenerate "racer is exactly on the closest point" case to
      // avoid divide-by-zero. With distSq = 0 the racer is INSIDE the
      // triangle; rare on a single-frame transition since the previous
      // frame would have caught it on the boundary.
      if (TMP_CLOSEST.distSq < 1e-6) continue;

      const dist = Math.sqrt(TMP_CLOSEST.distSq);
      const nx = (px - TMP_CLOSEST.x) / dist;
      const nz = (pz - TMP_CLOSEST.z) / dist;
      position.x = TMP_CLOSEST.x + nx * (racerRadius + 0.02);
      position.z = TMP_CLOSEST.z + nz * (racerRadius + 0.02);

      const nWallX = -nx;
      const nWallZ = -nz;
      const vDotN = velocity.x * nWallX + velocity.z * nWallZ;
      const impactSpeed = Math.max(0, vDotN);
      if (vDotN > 0) {
        const tx = velocity.x - vDotN * nWallX;
        const tz = velocity.z - vDotN * nWallZ;
        const perpX = -vDotN * nWallX * (1 - perpDamping);
        const perpZ = -vDotN * nWallZ * (1 - perpDamping);
        velocity.x = tx + perpX;
        velocity.z = tz + perpZ;
      }
      return { hit: true, impactSpeed, nx: nWallX, nz: nWallZ };
    }
  }
  return { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
}

export function resolveTrackCorridorCollision(
  position: THREE.Vector3,
  velocity: THREE.Vector3,
  track: Track,
  perpDamping = 0.3,
  vehicleClearance = 0,
  sweep?: MovementSegment2D,
): WallHit {
  const sweptHit = resolveSweptTrackCorridorCollision(
    position,
    velocity,
    track,
    perpDamping,
    vehicleClearance,
    sweep,
  );
  if (sweptHit) return sweptHit;

  const pos = locateTrackPosition(track, position.x, position.z);
  const clearance = Math.max(0, Math.min(vehicleClearance, track.wallBuffer - 0.1));
  const leftLimit = pos.widthLeft + track.wallBuffer - clearance;
  const rightLimit = -pos.widthRight - track.wallBuffer + clearance;

  let wallNormalX = 0;
  let wallNormalZ = 0;
  let boundarySigned = 0;

  if (pos.signedLateral > leftLimit) {
    wallNormalX = pos.nx;
    wallNormalZ = pos.nz;
    boundarySigned = leftLimit;
  } else if (pos.signedLateral < rightLimit) {
    wallNormalX = -pos.nx;
    wallNormalZ = -pos.nz;
    boundarySigned = rightLimit;
  } else {
    return { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
  }

  position.x = pos.closestX + pos.nx * boundarySigned - wallNormalX * 0.05;
  position.z = pos.closestZ + pos.nz * boundarySigned - wallNormalZ * 0.05;

  const vDotN = velocity.x * wallNormalX + velocity.z * wallNormalZ;
  const impactSpeed = Math.max(0, vDotN);
  const tx = velocity.x - vDotN * wallNormalX;
  const tz = velocity.z - vDotN * wallNormalZ;
  let perpX = 0;
  let perpZ = 0;
  if (vDotN > 0) {
    perpX = -vDotN * wallNormalX * (1 - perpDamping);
    perpZ = -vDotN * wallNormalZ * (1 - perpDamping);
  }
  velocity.x = tx + perpX;
  velocity.z = tz + perpZ;

  return { hit: true, impactSpeed, nx: wallNormalX, nz: wallNormalZ };
}

function resolveSweptTrackCorridorCollision(
  position: THREE.Vector3,
  velocity: THREE.Vector3,
  track: Track,
  perpDamping: number,
  vehicleClearance: number,
  sweep: MovementSegment2D | undefined,
): WallHit | null {
  if (!validMovementSegment(sweep)) return null;

  const clearance = Math.max(0, Math.min(vehicleClearance, track.wallBuffer - 0.1));
  const start = locateTrackPosition(track, sweep.fromX, sweep.fromZ);
  const end = locateTrackPosition(track, sweep.toX, sweep.toZ);
  const startLeftLimit = start.widthLeft + track.wallBuffer - clearance;
  const endLeftLimit = end.widthLeft + track.wallBuffer - clearance;
  const startRightLimit = -start.widthRight - track.wallBuffer + clearance;
  const endRightLimit = -end.widthRight - track.wallBuffer + clearance;
  const startLeftViolation = start.signedLateral - startLeftLimit;
  const endLeftViolation = end.signedLateral - endLeftLimit;
  const startRightViolation = startRightLimit - start.signedLateral;
  const endRightViolation = endRightLimit - end.signedLateral;

  let t = Number.POSITIVE_INFINITY;
  let side: 1 | -1 | 0 = 0;
  if (startLeftViolation <= 0 && endLeftViolation > 0) {
    t = startLeftViolation / (startLeftViolation - endLeftViolation);
    side = 1;
  }
  if (startRightViolation <= 0 && endRightViolation > 0) {
    const rightT = startRightViolation / (startRightViolation - endRightViolation);
    if (rightT < t) {
      t = rightT;
      side = -1;
    }
  }
  if (side === 0 || !Number.isFinite(t)) return null;

  t = Math.max(0, Math.min(1, t));
  const hitX = sweep.fromX + (sweep.toX - sweep.fromX) * t;
  const hitZ = sweep.fromZ + (sweep.toZ - sweep.fromZ) * t;
  const hitPos = locateTrackPosition(track, hitX, hitZ);
  const boundarySigned = side > 0
    ? hitPos.widthLeft + track.wallBuffer - clearance
    : -hitPos.widthRight - track.wallBuffer + clearance;
  const wallNormalX = side > 0 ? hitPos.nx : -hitPos.nx;
  const wallNormalZ = side > 0 ? hitPos.nz : -hitPos.nz;

  position.x = hitPos.closestX + hitPos.nx * boundarySigned - wallNormalX * 0.05;
  position.z = hitPos.closestZ + hitPos.nz * boundarySigned - wallNormalZ * 0.05;

  const impactSpeed = applyIncomingWallDamping(velocity, wallNormalX, wallNormalZ, perpDamping);
  return { hit: true, impactSpeed, nx: wallNormalX, nz: wallNormalZ };
}
